import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const MFA = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple chars

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    // Simulate verification
    if (fullCode === '123456') { // Mock correct code
      navigate('/dashboard');
    } else {
      setError('Invalid code. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white flex items-center justify-center relative overflow-hidden">
      {/* Wood Texture Background */}
      <div
        className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=2670&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          filter: 'contrast(1.2) brightness(0.8)'
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

      {/* Authentication Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-md p-8 rounded-2xl bg-black/40 backdrop-blur-md border border-premium-gold/30 shadow-[0_0_50px_rgba(212,175,55,0.1)]"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-premium-gold flex items-center justify-center bg-black/50 text-premium-gold text-3xl shadow-lg">
            🔒
          </div>
          <h2 className="font-serif text-3xl font-bold text-premium-gold mb-2">Security Verification</h2>
          <p className="text-white/70 text-sm">
            Enter the 6-digit code sent to your authenticator app ensuring your account security.
          </p>
        </div>

        <div className="flex gap-2 justify-center mb-8">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-14 text-center text-2xl font-bold rounded-lg bg-white/5 border border-white/10 text-white focus:border-premium-gold focus:ring-1 focus:ring-premium-gold outline-none transition-all"
            />
          ))}
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-400 text-sm text-center mb-6 bg-red-400/10 py-2 rounded-lg border border-red-400/20"
          >
            {error}
          </motion.div>
        )}

        <button
          onClick={handleVerify}
          className="w-full py-4 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold text-lg rounded-xl hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-[1.02] active:scale-[0.98]"
        >
          Verify Access
        </button>

        <div className="mt-6 text-center">
          <button className="text-sm text-white/50 hover:text-premium-gold transition-colors">
            Resend Code
          </button>
          <span className="text-white/30 mx-2">|</span>
          <button onClick={() => navigate('/login')} className="text-sm text-white/50 hover:text-white transition-colors">
            Back to Login
          </button>
        </div>
      </motion.div>

      <div className="fixed bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-premium-wood-dark via-premium-gold to-premium-wood-dark z-50"></div>
    </div>
  );
};

export default MFA;